import { useState, useRef, useEffect } from "react";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { languageNames } from "@/locales";
import { ChevronDown, Globe, Check } from "lucide-react";

const LanguageToggle = () => {
  const { language, setLanguage, isAutoDetected, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const languages: Language[] = ["en", "de"];
  const currentLang = languageNames[language];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-background/60 backdrop-blur-md border border-white/10 hover:border-neon-purple/50 hover:shadow-lg hover:shadow-neon-purple/20 transition-all duration-300 ease-out text-sm font-medium group"
        aria-label={t("language.change")}
      >
        <Globe className="w-4 h-4 text-muted-foreground group-hover:text-neon-purple transition-colors duration-300" />
        <span className="text-xl">{currentLang.flag}</span>
        <span className="hidden sm:inline text-foreground">{currentLang.nativeName}</span>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`} 
        />
      </button>

      {/* Dropdown with premium glass-morphism */}
      <div
        className={`absolute top-full right-0 mt-3 w-56 rounded-2xl bg-background/95 backdrop-blur-xl border border-white/10 shadow-xl shadow-black/30 overflow-hidden transition-all duration-300 ease-out z-50 ${
          isOpen 
            ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" 
            : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
        }`}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/5 via-transparent to-neon-cyan/5 pointer-events-none" />
        
        {/* Header */}
        <div className="relative px-4 py-3 border-b border-white/10">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("language.change")}
          </span>
        </div>

        {/* Language options */}
        <div className="relative py-2">
          {languages.map((lang) => {
            const langInfo = languageNames[lang];
            const isSelected = language === lang;
            
            return (
              <button
                key={lang}
                onClick={() => {
                  setLanguage(lang);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${
                  isSelected 
                    ? "bg-gradient-to-r from-neon-purple/20 to-neon-cyan/10 border-l-2 border-neon-purple text-foreground" 
                    : "text-muted-foreground hover:bg-white/5 hover:text-foreground hover:pl-5"
                }`}
              >
                <span className="text-2xl">{langInfo.flag}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm">{langInfo.nativeName}</div>
                  <div className="text-xs text-muted-foreground">{langInfo.name}</div>
                </div>
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-neon-purple/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-neon-purple" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Auto-detected badge */}
        {isAutoDetected && (
          <div className="relative px-4 py-2.5 border-t border-white/10 bg-gradient-to-r from-neon-cyan/10 to-transparent">
            <span className="text-xs text-neon-cyan flex items-center gap-1.5">
              <Globe className="w-3 h-3" />
              {t("language.auto")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageToggle;
